import { useEffect, useRef, useState } from "react";

const vertexShader = `
  attribute vec2 aPosition;

  void main() {
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;

  uniform vec2 uResolution;
  uniform vec2 uPointer;
  uniform float uTime;
  uniform float uScroll;

  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash21(i), hash21(i + vec2(1.0, 0.0)), f.x),
      mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0)), f.x),
      f.y
    );
  }

  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;
    mat2 turn = mat2(0.80, -0.60, 0.60, 0.80);
    for (int i = 0; i < 4; i++) {
      value += amplitude * noise(p);
      p = turn * p * 2.03 + 17.17;
      amplitude *= 0.5;
    }
    return value;
  }

  void main() {
    vec2 resolution = max(uResolution, vec2(1.0));
    vec2 p = (2.0 * gl_FragCoord.xy - resolution.xy) / min(resolution.x, resolution.y);
    vec2 pointer = (uPointer * 2.0 - 1.0);
    pointer.x *= resolution.x / resolution.y;

    float t = uTime * 0.075;
    p += (pointer - p) * 0.025;

    float tunnel = clamp(uScroll, 0.0, 2.2);
    float tunnelRadius = length(p);
    float tunnelAngle = atan(p.y, p.x);
    tunnelAngle += tunnel * (0.15 + (1.35 - tunnelRadius) * 0.055);
    tunnelRadius *= 1.0 - tunnel * 0.075;
    tunnelRadius += sin(tunnelRadius * 12.0 - tunnel * 4.6) * tunnel * 0.012;
    p = vec2(cos(tunnelAngle), sin(tunnelAngle)) * tunnelRadius;

    vec2 firstWarp = vec2(
      fbm(p * 0.72 + vec2(t, -t * 0.57)),
      fbm(p * 0.72 + vec2(4.8 - t * 0.46, 1.3 + t * 0.32))
    );
    vec2 secondWarp = vec2(
      fbm(p * 1.08 + firstWarp * 2.6 + vec2(1.7, 8.2)),
      fbm(p * 1.08 - firstWarp * 2.1 + vec2(8.3, 2.8))
    );

    vec2 silk = p + (firstWarp - 0.5) * 2.35 + (secondWarp - 0.5) * 0.72;
    float field = fbm(silk * 1.16 + vec2(t * 0.22, -t * 0.18));
    float detail = fbm(silk * 2.25 - firstWarp * 1.4);

    float wave = sin((silk.x * 0.68 + silk.y * 0.22 + field * 2.35) * 5.4 - t * 2.0);
    float fold = 0.5 + 0.5 * sin((field * 1.8 + detail * 0.72 + silk.y * 0.08) * 19.0);
    float caustic = pow(max(0.0, 1.0 - abs(wave)), 12.0);
    float shadow = smoothstep(0.02, 0.96, fold);

    vec3 bone = vec3(0.98, 0.91, 0.68);
    vec3 acid = vec3(0.75, 1.00, 0.02);
    vec3 cobalt = vec3(0.01, 0.10, 0.96);
    vec3 orchid = vec3(0.62, 0.02, 0.91);
    vec3 coral = vec3(1.00, 0.08, 0.34);
    vec3 ink = vec3(0.015, 0.012, 0.025);

    vec3 color = mix(bone, acid, smoothstep(0.12, 0.72, field));
    color = mix(color, cobalt, smoothstep(0.34, 0.94, secondWarp.x));
    color = mix(color, coral, smoothstep(0.50, 0.90, firstWarp.y) * (1.0 - secondWarp.x));
    color = mix(color, orchid, smoothstep(0.64, 0.96, detail) * 0.72);

    vec3 spectrum = 0.52 + 0.48 * cos(
      6.28318 * (field * 1.45 + detail * 0.44 + vec3(0.00, 0.19, 0.43))
    );
    color = mix(color, spectrum, 0.28 + caustic * 0.18);
    color = mix(ink, color, 0.40 + shadow * 0.86);
    color += caustic * mix(vec3(1.0), acid, 0.32) * 0.78;

    float lens = exp(-length(p - pointer) * 1.35);
    color = mix(color, color.gbr, lens * 0.12);

    float vignette = 1.0 - smoothstep(0.65, 1.8, length(p * vec2(0.72, 1.0)));
    color *= 0.84 + vignette * 0.22;

    float grain = hash21(gl_FragCoord.xy + fract(uTime) * 91.7) - 0.5;
    color += grain * 0.025;

    color = pow(max(color, 0.0), vec3(0.92));
    gl_FragColor = vec4(color, 1.0);
  }
`;

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

type SilkFieldProps = {
  active?: boolean;
  onReady?: () => void;
};

export function SilkField({ active = true, onReady }: SilkFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeRef = useRef(active);
  const onReadyRef = useRef(onReady);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      depth: false,
      powerPreference: "high-performance",
    });
    if (!gl) return;
    const context = gl;

    const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexShader);
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentShader);
    if (!vertex || !fragment) return;

    const program = gl.createProgram();
    const buffer = gl.createBuffer();
    if (!program || !buffer) return;

    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    const position = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const resolutionLocation = gl.getUniformLocation(program, "uResolution");
    const pointerLocation = gl.getUniformLocation(program, "uPointer");
    const timeLocation = gl.getUniformLocation(program, "uTime");
    const scrollLocation = gl.getUniformLocation(program, "uScroll");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pointer = { x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5 };
    const scroll = {
      current: window.scrollY / Math.max(window.innerHeight, 1),
      target: window.scrollY / Math.max(window.innerHeight, 1),
    };
    let width = 1;
    let height = 1;
    let frame = 0;
    let visible = !document.hidden;
    let inView = true;
    const start = performance.now();

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const cssPixels = bounds.width * bounds.height;
      const quality = cssPixels > 1_100_000 ? 0.78 : cssPixels > 520_000 ? 0.86 : 0.92;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.25) * quality;
      width = Math.max(1, Math.round(bounds.width * ratio));
      height = Math.max(1, Math.round(bounds.height * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    function schedule() {
      if (reduceMotion || !visible || !inView || !activeRef.current || frame !== 0) return;
      frame = window.requestAnimationFrame(draw);
    }

    function draw(now: number) {
      frame = 0;
      if (activeRef.current && (inView || reduceMotion)) {
        pointer.x += (pointer.targetX - pointer.x) * 0.045;
        pointer.y += (pointer.targetY - pointer.y) * 0.045;
        scroll.current += (scroll.target - scroll.current) * 0.065;
        context.uniform2f(resolutionLocation, width, height);
        context.uniform2f(pointerLocation, pointer.x, pointer.y);
        context.uniform1f(timeLocation, (now - start) * 0.001);
        context.uniform1f(scrollLocation, reduceMotion ? 0 : scroll.current);
        context.drawArrays(context.TRIANGLES, 0, 3);
      }
      schedule();
    }

    const handlePointer = (event: PointerEvent) => {
      if (!inView) return;
      const bounds = canvas.getBoundingClientRect();
      pointer.targetX = (event.clientX - bounds.left) / Math.max(bounds.width, 1);
      pointer.targetY = 1 - (event.clientY - bounds.top) / Math.max(bounds.height, 1);
    };
    const handleScroll = () => {
      scroll.target = window.scrollY / Math.max(window.innerHeight, 1);
    };
    const handleVisibility = () => {
      visible = !document.hidden;
      if (!visible && frame !== 0) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      } else {
        schedule();
      }
    };

    const resizeObserver = new ResizeObserver(resize);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inView = entry?.isIntersecting ?? true;
      if (!inView && frame !== 0) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      } else {
        schedule();
      }
    }, { rootMargin: "20% 0px" });
    resizeObserver.observe(canvas);
    intersectionObserver.observe(canvas);
    window.addEventListener("pointermove", handlePointer, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibility);
    resize();
    draw(reduceMotion ? start + 9_000 : performance.now());
    setReady(true);
    onReadyRef.current?.();

    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener("pointermove", handlePointer);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibility);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, []);

  return (
    <div
      className="silk-field"
      data-active={active ? "true" : "false"}
      data-ready={ready ? "true" : undefined}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} />
      <div className="silk-field__fallback" />
    </div>
  );
}
